import { getPossessive } from '@/pages/CompanyReport/utils/grammar';
import useCompanyVariant from '@/hooks/useCompany/useCompanyVariant';

export enum SeoTitleDescriptionTestVariant {
  Control = 0,
  Variant = 1,
}

export function useRolloutVariant(
  canonicalUrlWithSection: string,
  saltSuffix: string
): SeoTitleDescriptionTestVariant {
  const least = useCompanyVariant(canonicalUrlWithSection + saltSuffix);
  if (parseInt(least, 16) <= 7) {
    return SeoTitleDescriptionTestVariant.Control;
  }
  return SeoTitleDescriptionTestVariant.Variant;
}

export const getTitle = ({
  companyName,
  companyShortName,
  tickerSymbol,
  exchangeTicker,
  uniqueSymbol,
  section,
  variant,
}: {
  companyName: string;
  companyShortName: string;
  tickerSymbol: string;
  exchangeTicker: string;
  uniqueSymbol: string;
  section: string;
  variant: SeoTitleDescriptionTestVariant;
}) => {
  switch (section) {
    case 'summary':
      return `${companyShortName} (${exchangeTicker}) - Stock Price, News & Analysis`;
    case 'valuation':
      return [
        `${companyName} (${uniqueSymbol}) Stock Valuation`,
        `${companyShortName} (${uniqueSymbol}) Stock Valuation, Peer Comparison & Price Targets`,
      ][variant];
    case 'future':
      return [
        `${companyShortName} (${tickerSymbol}) Analysts Prediction, Stock Forecast & Price Targets`,
        `${companyShortName} (${uniqueSymbol}) Stock Forecast & Analyst Predictions`,
      ][variant];
    case 'past':
      return [
        `${companyName} (${uniqueSymbol}) Performance Analysis`,
        `${companyShortName} (${uniqueSymbol}) - Past Earnings & Revenue Performance`,
      ][variant];
    case 'health':
      return [
        `${companyName} (${uniqueSymbol}) Health & Balance Sheet`,
        `${companyShortName} (${tickerSymbol}) Balance Sheet & Financial Health Metrics`,
      ][variant];
    case 'dividend':
      return `${companyShortName} (${exchangeTicker}) Dividend Yield, History and Growth`;
    case 'management':
      return `${companyName} (${tickerSymbol}) Leadership & Management Team Analysis`;
    case 'ownership':
      return `${companyName} Insider Trading & Ownership Structure`;
    case 'information':
      return `${companyName} (${tickerSymbol}) Company Information`;
    default:
      return `${companyName} (${tickerSymbol})`;
  }
};

export const getDescription = ({
  companyName,
  companyShortName,
  tickerSymbol,
  exchangeTicker,
  fundamentals,
  section,
  latestDividendYield,
  latestDividendYieldFormatted,
  dividendGrowing,
  dividendCovered,
  currentDividendPayoutRatioFormatted,
  isBank,
  variant,
}: {
  companyName: string;
  companyShortName: string;
  tickerSymbol: string;
  exchangeTicker: string;
  fundamentals: string;
  section: string;
  latestDividendYield: number | null;
  latestDividendYieldFormatted: string;
  dividendGrowing: boolean;
  dividendCovered: boolean;
  currentDividendPayoutRatioFormatted: string;
  isBank: boolean;
  variant: SeoTitleDescriptionTestVariant;
}) => {
  switch (section) {
    case 'summary':
      return [
        `Should you invest in ${companyShortName} (${exchangeTicker})? ${fundamentals}`,
        `Research ${getPossessive(
          companyShortName
        )} (${exchangeTicker}) stock price, latest news & stock analysis. Find everything from its Valuation, Future Growth, Past Performance and more.`,
      ][variant];
    case 'valuation':
      return [
        `Learn to value ${companyName} (${tickerSymbol}) stock with easy-to-understand analysis. From popular relative valuation methods to future cashflows and analyst forecasts.`,
        `Research ${getPossessive(
          companyShortName
        )} (${exchangeTicker}) stock key valuation metrics while comparing it with its industry peers & market side by side.`,
      ][variant];
    case 'future':
      return [
        `${companyShortName} (${exchangeTicker}) earnings and revenue forecasts, price targets, future return on equity. Compare ${companyName}'s growth forecast against it's industry peers.`,
        `Discover ${getPossessive(
          companyShortName
        )} earnings and revenue growth rates, forecasts, and the latest analyst predictions while comparing them to its industry peers.`,
      ][variant];
    case 'past':
      return [
        `Learn how to understand ${companyName} (${tickerSymbol}) stock's past performance and history. Compare popular metrics against industry and market.`,
        `Discover ${getPossessive(
          companyShortName
        )} stock performance and compare it with industry peers. From earnings and revenue to ROE, growth rates, and profit margins.`,
      ][variant];
    case 'health':
      if (variant === SeoTitleDescriptionTestVariant.Control) {
        return `Learn how to understand ${companyName} (${tickerSymbol}) stock's financial health, balance sheet and how this can help you make an informed decision.`;
      }
      if (isBank) {
        return `Discover ${getPossessive(
          companyShortName
        )} balance sheet and financial health metrics. Everything from Total Assets, Equity, and Cash-on-Hand to Bad Loan Levels and Debt.`;
      }
      return `Discover ${getPossessive(
        companyShortName
      )} balance sheet and financial health metrics. From total debt, total equity, assets to cash-on-hand, interest coverage and more.`;
    case 'dividend':
      if (latestDividendYield === 0 || latestDividendYield === null) {
        return 'Company does not have a record of paying a dividend.';
      } else {
        return `${companyShortName} (${exchangeTicker}) dividend yield is ${latestDividendYieldFormatted}. Dividend payments have ${
          dividendGrowing ? 'increased' : 'decreased'
        } over the last 10 years and are ${
          dividendCovered ? 'covered' : 'not covered'
        } by earnings with a payout ratio of ${currentDividendPayoutRatioFormatted}.`;
      }
    case 'management':
      return `Learn about ${companyName} (${tickerSymbol}) stock's management team. Comprehensive performance, salary and tenure analysis for the CEO, board and leadership team.`;
    case 'ownership':
      return `Uncover the latest insider trading activity for ${companyName} (${tickerSymbol}). Know which insiders are buying and selling along with top shareholders and ownership breakdown.`;
    case 'information':
      return `Learn more about ${companyName} (${tickerSymbol}). From their employee growth over the years and their exchange listings.`;
    default:
      return `${companyName} (${tickerSymbol})`;
  }
};
